import { deepCompare } from 'client/utils/deepCompare'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BodyInterface } from 'common/types/entities/body-interface'
import ServerPageInterface from 'src/shared/types/server-page-interface'

/* NOTE: https://app.shortcut.com/systeme/story/55619/remove-body-duplicates-of-entities
 * removing duplicated bodies
 *
 * ------CASE 1--------
 * entities: {
 *  bodyEntity1Copy: {
 *    id: bodyEntity1Copy,
 *    field1: value1,
 *    field2: value2,
 *  },
 *  bodyEntity1: {
 *    id: bodyEntity1,
 *    field1: value1,
 *    field2: value2,
 *  },
 *  ...
 * }
 * The bodyEntity1 and bodyEntity1Copy have the same fields, except "id", therefore we have to remove bodyEntity1Copy
 * --------------------
 *
 * ------CASE 2--------
 * entities: {
 *  bodyEntity1Copy: {
 *    id: bodyEntity1Copy,
 *    field1: value1,
 *    field2: value2,
 *  },
 *  bodyEntity1: {
 *    id: bodyEntity1,
 *    field1: value1,
 *    field2: value2,
 *  },
 *  bodyEntity2Copy: {
 *    id: bodyEntity2Copy,
 *    field3: value3,
 *    field4: value4,
 *  },
 *  bodyEntity2: {
 *    id: bodyEntity2,
 *    field3: value3,
 *    field4: value4,
 *  },
 *  ...
 * }
 * The bodyEntity1 and bodyEntity1Copy have the same fields (except "id"), therefore we have to remove bodyEntity1Copy
 * The bodyEntity2 and bodyEntity2Copy have the same fields (except "id"), therefore we have to remove bodyEntity2Copy
 * We can't remove bodyEntity1 and bodyEntity2 because we don't know which is right
 * (I'm not sure this is frequent case)
 * --------------------
 */

export default function removeDuplicatedBodies(
  page: ServerPageInterface,
): [ServerPageInterface, boolean] {
  let needChangeContent = false
  const originalEntities = page.content.entities
  const bodies = Object.values(originalEntities).filter(
    entity => entity.type === EntityTypeEnum.Body,
  ) as unknown as BodyInterface[]

  bodies.forEach((bodyValueToCompare, indexToCompare) => {
    const { id: _, ...bodyValueToCompareWithoutId } = bodyValueToCompare
    bodies.some((bodyValue, index) => {
      if (indexToCompare < index) {
        const { id: __, ...bodyValueWithoutId } = bodyValue
        const isDuplicated = deepCompare(
          bodyValueToCompareWithoutId,
          bodyValueWithoutId,
        )
        if (isDuplicated) {
          needChangeContent = true
          delete originalEntities[bodyValueToCompare.id]
          return isDuplicated
        }
      }
      return false
    })
  })

  if (bodies.length > 1) {
    throw new Error(`Unresolved several bodies, page id=${page.id}`)
  }

  return [page, needChangeContent]
}
